import _ from 'lodash';
import { getCurrentInstance } from 'vue-demi';
import { useToastV3 } from '@onereach/ui-components-25';

import { useHelpers } from '@/composables';
import { useAppConfig } from '@/apps/agent-ui/composables/use-config-store';

/**
 * Enhanced local storage settings with proper user/account isolation.
 * Prioritizes local storage for fast startup, with background API updates.
 */
export const useLocalSettings = () => {
  const { userId, environmentEnv, environmentClient } = useHelpers();
  const { isDebug } = useAppConfig();
  const vm = getCurrentInstance()!;

  /**
   * Generate a consistent cache key that includes environment, client, and userId
   * for uniqueness across different environments and clients
   */
  const getCacheKey = (key: string): string => {
    const env = environmentEnv.value || 'unknown';
    const client = environmentClient.value || 'unknown';
    return `HITL-${key}:${client}:${env}:${userId.value}`;
  };

  /**
   * Update settings in background without blocking the UI
   */
  const updateSettingsInBackground = async <T>(args: {
    key: string,
    apiRequest: () => Promise<T>,
    currentSettings: T,
    notifyMessage?: string,
    filterForCache?: (settings: T) => T,
    /** When provided, used instead of _.isEqual to decide if settings changed (e.g. to ignore volatile fields) */
    areSettingsEqual?: (cached: T | null | undefined, fresh: T | null | undefined) => boolean,
    /** When provided and returns false, cache is updated but no toast is shown (silent apply) */
    shouldNotifyOnMismatch?: (cached: T | null | undefined, fresh: T | null | undefined) => boolean,
    onCacheMismatch?: (fresh: T, cached: T) => void,
    onRequestFailed?: () => void,
    onRequestComplete?: (settings: T) => void,
  }) => {
    const {
      key, apiRequest, currentSettings,
      notifyMessage,
      filterForCache = _.identity,
      areSettingsEqual,
      shouldNotifyOnMismatch,
      onCacheMismatch = () => {},
      onRequestFailed = () => {},
      onRequestComplete = () => {},
    } = args;

    try {
      const response = await apiRequest();
      const filteredResponse = filterForCache(response);

      const hasChanged = areSettingsEqual
        ? !areSettingsEqual(currentSettings, filteredResponse)
        : !_.isEqual(currentSettings, filteredResponse);

      // Check if settings have changed
      if (hasChanged) {
        setCachedSettings(key, filteredResponse);

        const showToast = !shouldNotifyOnMismatch || shouldNotifyOnMismatch(currentSettings, filteredResponse);
        if (showToast && notifyMessage) {
          useToastV3().notify({
            messageText: notifyMessage,
            link: {
              title: vm.proxy.$t('HITL.common.toast.update-now') as string,
              href: '',
              callback: () => {
                window.location.reload();
              },
            },
            dismissible: true,
          }, { duration: 0 });
        }

        onCacheMismatch && onCacheMismatch(filteredResponse!, currentSettings!);

        isDebug.value && console.debug('[DEBUG] Settings updated in background', key);
      }

      onRequestComplete && onRequestComplete(response);
    } catch (error) {
      console.error('[Local Settings] Background update failed', error);
      onRequestFailed && onRequestFailed();
    }
  };

  /**
   * Get cached settings only, without making API calls
   */
  const getCachedSettings = <T>(key: string): T | null => {
    const userKey = getCacheKey(key);
    const cachedData = localStorage.getItem(userKey);

    if (cachedData) {
      try {
        const settings = JSON.parse(cachedData);
        if (!_.isEmpty(settings)) {
          isDebug.value && console.debug('[DEBUG] Returning cached settings only', {userKey, settings});
          return settings as T;
        }
      } catch (error) {
        console.error('[Local Settings] Failed to parse cached data', error);
      }
    }

    isDebug.value && console.debug('[DEBUG] No cached settings found', userKey);
    return null;
  };

  /**
   * Set cached settings only, without making API calls
   */
  const setCachedSettings = <T>(key: string, settings: T): void => {
    const userKey = getCacheKey(key);
    try {
      localStorage.setItem(userKey, JSON.stringify(settings, null, 2));
      isDebug.value && console.debug('[DEBUG] Cached settings updated', {userKey, settings});
    } catch (error) {
      console.error('[Local Settings] Failed to cache settings', error);
    }
  };

  /**
   * Update cached settings using a transformer function
   */
  const updateCachedSettings = <T>(key: string, transformer: (currentSettings: T) => T): void => {
    const userKey = getCacheKey(key);
    try {
      const currentSettings = getCachedSettings<T>(key);
      if (currentSettings !== null) {
        const updatedSettings = transformer(currentSettings);
        setCachedSettings(key, updatedSettings);
      } else {
        isDebug.value && console.debug('[DEBUG] No cached settings found to update', userKey);
      }
    } catch (error) {
      console.error('[Local Settings] Failed to update cached settings via transformer', error);
    }
  };

  /**
   * Delete cached settings completely
   */
  const deleteCachedSettings = (key: string): void => {
    const userKey = getCacheKey(key);
    try {
      localStorage.removeItem(userKey);
      isDebug.value && console.debug('[DEBUG] Cached settings deleted', {userKey});
    } catch (error) {
      console.error('[Local Settings] Failed to delete cached settings', error);
    }
  };

  /**
   * Get settings with immediate local storage return and optional background refresh
   */
  const getSettingsWithCache = async <T>(args: {
    key: string;
    category?: string;
    apiRequest: () => Promise<T>;
    filterForCache?: (settings: T) => T;
    /** If we don't like cached data skip it and wait for actual request to complete */
    isCacheValid?: (settings?: T) => boolean;
    /** When provided, used to decide if cached and fresh settings are equivalent (avoids false "settings changed" toasts) */
    areSettingsEqual?: (cached: T | null | undefined, fresh: T | null | undefined) => boolean;
    /** When provided and returns false on mismatch, cache is updated but no toast is shown */
    shouldNotifyOnMismatch?: (cached: T | null | undefined, fresh: T | null | undefined) => boolean;
    onCacheMismatch?: (fresh: T, cached: T) => void;
    onBackgroundRefreshFailed?: () => void;
    onBackgroundRefreshComplete?: (settings: T) => void;
  }) => {
    const {
      key, category, apiRequest, filterForCache = _.identity,
      isCacheValid = () => true,
      areSettingsEqual,
      shouldNotifyOnMismatch,
      onCacheMismatch = () => {},
      onBackgroundRefreshFailed = () => {},
      onBackgroundRefreshComplete = () => {},
    } = args;

    const cachedSettings = getCachedSettings<T>(key);
    if (cachedSettings && isCacheValid(cachedSettings)) {
      isDebug.value && console.debug('[DEBUG] Returning cached settings immediately', {key, settings: cachedSettings});

      updateSettingsInBackground({
        key, apiRequest,
        currentSettings: cachedSettings,
        notifyMessage : category ? vm.proxy.$t('HITL.common.toast.notify-settings-change', { category }) as string : undefined,
        onCacheMismatch,
        filterForCache,
        areSettingsEqual,
        shouldNotifyOnMismatch,
        onRequestFailed: onBackgroundRefreshFailed,
        onRequestComplete: onBackgroundRefreshComplete,
      });

      return cachedSettings;
    }

    // Fallback to API call
    isDebug.value && console.debug('[DEBUG] Requesting fresh settings', key);
    const response = await apiRequest();
    const filteredResponse = filterForCache(response);
    setCachedSettings(key, filteredResponse);
    return response;
  };

  return {
    getCacheKey,
    getCachedSettings,
    setCachedSettings,
    updateCachedSettings,
    deleteCachedSettings,
    getSettingsWithCache
  };
};
